// The Virtual Visit join link: which Zoom links we accept, and how to keep them away from models.
// ONE source, spliced into every copy by scripts/sync-visit-join-link.mjs (S-171 compares them).
// Spec: docs/specs/2026-10-07-virtual-visit-join-link.md. ASCII only: the regions are spliced into
// workflow JSON files that store non-ASCII as \u escapes.

// VISIT-JOIN-RULES-START
// The clinicians' standing Zoom rooms: meeting ids only, never a passcode. A room change is a reviewed
// code change (spec D7) - never $vars, never a DB value - so a forged link to any other meeting fails.
const KNOWN_ROOMS = new Set(['2923323278', '3994016252', '6388013965']);
// Whole string, bare host, no i/g/u flag. The regex is the whole decision: no new URL(), which the
// corp n8n Code sandbox is not known to provide (class #4).
const JOIN_URL_RE = /^https:\/\/zoom\.us\/j\/(\d{9,11})(?:\?pwd=[A-Za-z0-9]{16,64}(?:\.\d{1,2})?)?$/;
function isJoinUrl(u) {
  if (typeof u !== 'string') return false;
  const m = JOIN_URL_RE.exec(u);
  return !!m && KNOWN_ROOMS.has(m[1]);
}
// A sentence holding the link -> the link, or null. A token is never trimmed into a shorter link;
// two different links, or an unknown room, give null.
function joinUrlOf(text) {
  if (typeof text !== 'string') return null;
  const found = new Set();
  for (const raw of text.split(/\s+/)) {
    if (!raw) continue;
    // \x22/\x27, not the quote characters: a literal quote here derails the statics' comment stripper.
    const tok = /[.,;:)\]>\x22\x27]$/.test(raw) ? raw.slice(0, -1) : raw;
    if (isJoinUrl(tok)) found.add(tok);
  }
  return found.size === 1 ? [...found][0] : null;
}
function durationOf(d) {
  const n = (typeof d === 'string' && /^\d+$/.test(d)) ? Number(d) : d;
  return (Number.isInteger(n) && n >= 5 && n <= 240) ? n : null;
}
// VISIT-JOIN-RULES-END

// VISIT-JOIN-NEUTRALIZE-START
// Anything handed to a model or to Google Chat. Patients quote links back (class #29), so every sender.
// NFKC first, format and control characters DELETED (one inside the host would otherwise split the
// token around the test). Token-wise, so no backtracking on long unspaced text.
const ZOOM_TOKEN_RE = /zoom\.us|zoom\.com|zoomgov\.com|zoom%2eus|zoom%2ecom|zoom\[\.\]|zoom\(\.\)|pwd=|pwd%3d/i;
// No lookbehind: the back-office bundle imports this module, and older Safari cannot compile one.
const ROOM_ID_RES = [...KNOWN_ROOMS].map((id) => new RegExp('(^|[^0-9])' + id.split('').join('[ ._-]?') + '(?![0-9])', 'g'));
function neutralizeZoom(text) {
  if (typeof text !== 'string' || !text) return text;
  const n = text.normalize('NFKC').replace(/[\p{Cf}\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/gu, '');
  let s = n.split(/(\s+)/).map((tok) => (ZOOM_TOKEN_RE.test(tok) ? '[video link]' : tok)).join('');
  for (const re of ROOM_ID_RES) s = s.replace(re, '$1[video link]');
  // Nothing found: the caller gets its own text back byte for byte (prompts are pinned by hash).
  return s === n ? text : s;
}
// Rows and bundles: every string, recursively, incl. a context_json stored as a JSON string.
// Past the depth bound nothing is passed through as-is: an over-deep value becomes a placeholder.
const MAX_DEEP = 12;
// The care team's note to the patient (a review task's context_json.lab_note) is clinical free text: a label only.
const LAB_NOTE_PLACEHOLDER = '[care team note on the lab results]';
function neutralizeDeep(v, depth) {
  const d = depth || 0;
  if (typeof v === 'string') {
    const t = v.trim();
    if ((t[0] === '{' || t[0] === '[') && t.length < 200000) {
      try {
        const parsed = JSON.parse(t);
        const clean = JSON.stringify(neutralizeDeep(parsed, d + 1));
        // Unchanged JSON keeps its own bytes (escaping, spacing); only a changed one is re-serialised.
        return clean === JSON.stringify(parsed) ? v : clean;
      } catch (e) { /* not JSON: plain text below */ }
    }
    // A clipped or double-encoded context_json can still carry the note: the whole string goes.
    if (/lab_note\\*["']?\s*:/.test(v)) return LAB_NOTE_PLACEHOLDER;
    return neutralizeZoom(v);
  }
  if (!v || typeof v !== 'object') return v;
  if (d > MAX_DEEP) return '[omitted]';
  if (Array.isArray(v)) return v.map((x) => neutralizeDeep(x, d + 1));
  const o = {};
  // Plain assignment: n8n's Code sandbox returns defineProperty keys as an empty object. An own "__proto__" key is dropped.
  for (const k of Object.keys(v)) {
    if (k === '__proto__') continue;
    o[k] = k === 'lab_note' && v[k] != null ? LAB_NOTE_PLACEHOLDER : neutralizeDeep(v[k], d + 1);
  }
  return o;
}
// VISIT-JOIN-NEUTRALIZE-END

// VISIT-JOIN-CARD-START
// The Join window and the one predicate every patient surface uses (card, banner, chat link, the
// back-office replica). Window on appointment.scheduled_at, never due_at.
const JOIN_OPENS_BEFORE_MS = 15 * 60000;
const JOIN_CLOSES_AFTER_MS = 10 * 60000;
const VISIT_CARD_CODE = 'onb_patient_virtual_visit';
const OPEN_CARD_STATUSES = ['pending', 'scheduled', 'in_progress', 'overdue'];
function joinWindow(scheduledAt, durationMin) {
  if (typeof scheduledAt !== 'string' || !/(Z|[+-]\d{2}:?\d{2})$/.test(scheduledAt)) return null;
  const start = Date.parse(scheduledAt);
  if (!Number.isFinite(start)) return null;
  const mins = durationOf(durationMin) || 45;
  return { opensAt: start - JOIN_OPENS_BEFORE_MS, closesAt: start + mins * 60000 + JOIN_CLOSES_AFTER_MS };
}
// -> { state: 'open'|'before'|'after'|'none', url }. url is set only when state is 'open'.
function visitJoinState(task, nowMs) {
  const none = { state: 'none', url: null };
  if (!task || String(task.template_code || '') !== VISIT_CARD_CODE) return none;
  if (!OPEN_CARD_STATUSES.includes(String(task.status || ''))) return none;
  let ctx = task.context_json;
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch (e) { return none; } }
  const ap = ctx && typeof ctx === 'object' ? ctx.appointment : null;
  if (!ap || ap.state !== 'booked' || !isJoinUrl(ap.join_url)) return none;
  const w = joinWindow(ap.scheduled_at, ap.duration_min);
  if (!w) return none;
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  if (now < w.opensAt) return { state: 'before', url: null };
  if (now >= w.closesAt) return { state: 'after', url: null };
  return { state: 'open', url: ap.join_url };
}
// A patient holds one visit card per appointment (spec 2026-10-26-recurring-virtual-visits, 4.11). One rule picks
// "the" visit on every surface: the card whose Join window is open, then the soonest start still ahead, then the
// lowest task_id. A card whose stamp or start cannot be read never wins.
function bookedStampOf(t) {
  if (!t || String(t.template_code || '') !== VISIT_CARD_CODE) return null;
  if (!OPEN_CARD_STATUSES.includes(String(t.status || ''))) return null;
  let ctx = t.context_json;
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch (e) { return null; } }
  const ap = ctx && typeof ctx === 'object' ? ctx.appointment : null;
  if (!ap || typeof ap !== 'object' || ap.state !== 'booked') return null;
  return Number.isFinite(Date.parse(ap.scheduled_at)) ? ap : null;
}
function pickVisitCard(cards, nowMs) {
  if (!Array.isArray(cards) || typeof nowMs !== 'number') return null;
  const idNum = (t) => { const n = Number(t.task_id); return Number.isFinite(n) ? n : Infinity; };
  let best = null, bestKey = null;
  for (const t of cards) {
    const ap = bookedStampOf(t);
    if (!ap) continue;
    const start = Date.parse(ap.scheduled_at);
    const w = joinWindow(ap.scheduled_at, ap.duration_min);
    const key = [w && nowMs >= w.opensAt && nowMs < w.closesAt ? 0 : 1, start >= nowMs ? start : Infinity, idNum(t)];
    if (!bestKey || key[0] < bestKey[0] || (key[0] === bestKey[0] && (key[1] < bestKey[1]
      || (key[1] === bestKey[1] && key[2] < bestKey[2])))) { best = t; bestKey = key; }
  }
  return best;
}
// VISIT-JOIN-CARD-END

// VISIT-JOIN-PICK-START
// WF2 lane 4's visit-link answer (spec 2026-10-08-wf2-visit-link-fact). The model sees the
// placeholder, never the link; Parse Reply inserts the card's link last, after every scan.
// Needs R (isJoinUrl, joinUrlOf), N (neutralizeZoom) and C (the card constants, joinWindow).
const JOIN_PLACEHOLDER = '[join link]';
// WF69's CHAT_HORIZON_MS: the link is never handed out earlier than WF69's own message would be.
const VISIT_LINK_HORIZON_MS = 14 * 86400000;
// Any bracketed text naming a link, so a model echo of [video link], of a re-feed label or of a
// mangled placeholder is caught as well as the real one. No g flag: a shared global regex keeps lastIndex.
const LINK_TOKEN_RE = /\[[^\[\]\n]{0,60}\blinks?\b[^\[\]\n]{0,60}\]/i;
// The companion's chat preview shows a body's first 90 characters; the link never sits there.
const LINK_PREVIEW_CHARS = 90;
// The companion's rule (pickVisitCard) over this patient's open cards of this care period; a card of another
// patient means the read is not what was asked for, so nothing. Then a known room, the 14-day horizon and the
// window not yet closed. -> { task_id, url, scheduled_at, uid } or null.
function visitCardOf(rows, uid, cpId, nowMs) {
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  if (!uid || !cpId) return null;
  const open = (Array.isArray(rows) ? rows : []).filter((t) => t && String(t.template_code || '') === VISIT_CARD_CODE
    && OPEN_CARD_STATUSES.includes(String(t.status || '')));
  if (open.some((t) => String(t.withings_user_id || '') !== String(uid))) return null;
  const t = pickVisitCard(open.filter((x) => x.id_care_period != null && String(x.id_care_period) === String(cpId)), now);
  if (!t) return null;
  const ap = bookedStampOf(t);
  if (!ap || !isJoinUrl(ap.join_url)) return null;
  const w = joinWindow(ap.scheduled_at, ap.duration_min);
  if (!w || now >= w.closesAt || Date.parse(ap.scheduled_at) - now > VISIT_LINK_HORIZON_MS) return null;
  return { task_id: String(t.task_id != null ? t.task_id : ''), url: ap.join_url, scheduled_at: ap.scheduled_at, uid: String(t.withings_user_id) };
}
// The placeholder as a whole token: start or whitespace before it; end, whitespace or one of .,;:)
// after it. Glued to anything else ("evil.co/?u=[join link]") it is refused.
function placeholderIsToken(text) {
  const i = text.indexOf(JOIN_PLACEHOLDER);
  if (i < 0 || text.indexOf(JOIN_PLACEHOLDER, i + 1) >= 0) return false;
  const before = i === 0 ? '' : text.charAt(i - 1);
  const after = text.slice(i + JOIN_PLACEHOLDER.length);
  if (before && !/\s/.test(before)) return false;
  return after === '' || /^\s/.test(after) || /^[.,;:)](\s|$)/.test(after);
}
// Every link token a text holds; the placeholder is legal only as the single token of a cited F4.
function linkTokensOf(text) { return String(text || '').match(new RegExp(LINK_TOKEN_RE.source, 'gi')) || []; }
// A domain- or path-shaped word ("evil.co", "x.io/r?to="), so nothing link-like stands beside the
// real link. An email is the contact rule's business, not this one's.
function hasLinkShape(text) {
  return String(text || '').split(/\s+/).some((raw) => {
    const tok = raw.replace(/[.,;:!?)\]\x22\x27]+$/, '');
    return tok.indexOf('@') < 0 && /^[\[(\x22\x27]*(?:https?:\/\/)?(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}(?:[\/?#:]\S*)?$/.test(tok);
  });
}
// The link last: past the chat preview, and ending its paragraph (one full stop at most).
function linkPlacementOk(text, token) {
  const i = text.indexOf(token);
  return i >= LINK_PREVIEW_CHARS && /^\.?(\n\n|$)/.test(text.slice(i + token.length));
}
// The compose cage's link rule, on the GSM-safe text that will be assembled. Without a cited F4 any
// link token refuses; with one, the placeholder must be the reply's single, whole, LAST token, past
// the preview, with nothing link-shaped beside it. A refusal sends the verbatim answers instead.
function linkCageHit(text, ids) {
  const t = String(text || '');
  const toks = linkTokensOf(t);
  if ((Array.isArray(ids) ? ids : []).indexOf('F4') < 0) return toks.length ? 'link_token' : '';
  if (toks.length !== 1 || toks[0] !== JOIN_PLACEHOLDER || !placeholderIsToken(t)) return 'link_token';
  if (hasLinkShape(t)) return 'link_neighbour';
  if (!/\[join link\]\.?$/.test(t.trim()) || !linkPlacementOk(t.trim(), JOIN_PLACEHOLDER)) return 'link_placement';
  return '';
}
// The last step before a body is written. -> { ok: true, message } or { ok: false, reason }.
function insertJoinLink(message, url) {
  if (typeof message !== 'string' || !isJoinUrl(url)) return { ok: false, reason: 'link_invalid' };
  const tokens = linkTokensOf(message);
  if (tokens.length !== 1 || tokens[0] !== JOIN_PLACEHOLDER) return { ok: false, reason: 'placeholder_count' };
  if (!placeholderIsToken(message)) return { ok: false, reason: 'placeholder_glued' };
  if (hasLinkShape(message)) return { ok: false, reason: 'link_neighbour' };
  if (!linkPlacementOk(message, JOIN_PLACEHOLDER)) return { ok: false, reason: 'link_placement' };
  // split/join, never String.replace: a replacement string's $ patterns would be interpreted.
  const out = message.split(JOIN_PLACEHOLDER).join(url);
  if (joinUrlOf(out) !== url) return { ok: false, reason: 'link_check' };
  if ((neutralizeZoom(out).match(/\[video link\]/g) || []).length !== 1) return { ok: false, reason: 'link_count' };
  if (linkTokensOf(out).length) return { ok: false, reason: 'placeholder_left' };
  return { ok: true, message: out };
}
// F4's sentence. Before the window, when Join opens; once it is open (the visit may have started), join now.
// Either way the placeholder ends it, at least LINK_PREVIEW_CHARS in, as the cage requires.
function joinFactOf(scheduledAt, nowMs) {
  const start = Date.parse(scheduledAt);
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  if (Number.isFinite(start) && now >= start - JOIN_OPENS_BEFORE_MS) {
    return 'Your video visit is open now: you can join it straight away with the Join button on your visit card or this link: ' + JOIN_PLACEHOLDER;
  }
  return 'Join your video visit from ' + Math.round(JOIN_OPENS_BEFORE_MS / 60000) + ' minutes before it starts, with the Join button on your visit card or this link: ' + JOIN_PLACEHOLDER;
}
// VISIT-JOIN-PICK-END

export {
  KNOWN_ROOMS, JOIN_URL_RE, isJoinUrl, joinUrlOf, durationOf,
  neutralizeZoom, neutralizeDeep,
  JOIN_OPENS_BEFORE_MS, JOIN_CLOSES_AFTER_MS, VISIT_CARD_CODE, joinWindow, visitJoinState, bookedStampOf, pickVisitCard,
  JOIN_PLACEHOLDER, VISIT_LINK_HORIZON_MS, LINK_TOKEN_RE, LINK_PREVIEW_CHARS, visitCardOf, placeholderIsToken, linkTokensOf,
  hasLinkShape, linkPlacementOk, linkCageHit, insertJoinLink, joinFactOf,
};
